import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { LayoutGroup, motion } from 'framer-motion';
import useAudio from '../../UseAudio';
import AnimatedCard from '../../common/AnimatedCard';
import AnimatedFaceDownHand from '../../common/AnimatedFaceDownHand';
import PlayerLabel from '../../common/PlayerLabel';
import RoomControls from '../../common/RoomControls';
import Celebration from '../trick-games/Celebration';
import '../../common/GameBoard.css';
import '../../common/Cards.css';
import '../trick-games/Overlays.css';


function DonkeyMatchOver({ gs, myId, send }) {
  const loserId = gs.match_loser;
  const isSpectator = myId === -1 || (gs.players && !gs.players[myId]);
  const iLost = myId === loserId;
  const iWon = !iLost && !isSpectator;

  const myPlayerObj = gs.players && gs.players[myId];
  const isHost = myPlayerObj && myPlayerObj.id === gs.host_id;
  const loserName = (gs.players && gs.players[loserId] && gs.players[loserId].name) || `Player ${loserId + 1}`;

  return (
    <>
      {iWon && <Celebration />}
      <motion.div 
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", bounce: 0.4, duration: 0.8 }}
        className={`match-over-panel ${iWon ? 'match-over-winner' : (isSpectator ? '' : 'match-over-loser')}`}
        style={{ filter: iWon || isSpectator ? 'none' : 'grayscale(0.2)', paddingBottom: '30px' }}
      >
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", bounce: 0.6 }}
          style={{ fontSize: 64, marginBottom: 8 }}
        >
          {iWon ? '🏆' : (isSpectator ? '👁' : '😔')}
        </motion.div>
        
        <h3 className={`match-over-title ${iWon ? 'title-winner' : (isSpectator ? '' : 'title-loser')}`}>
          {isSpectator ? 'Match Over' : (iWon ? 'You Won!' : 'You Lost!')}
        </h3>
        
        <p className="match-over-subtitle">
          {isSpectator ? (
            <span><strong>{loserName}</strong> is the Donkey!</span>
          ) : iWon ? (
            <span><strong>{loserName}</strong> is the Donkey!</span>
          ) : (
            <span>You are the Donkey!</span>
          )}
        </p>

        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          style={{ marginTop: 24 }}
        >
          {isHost ? (
            <button className="glow-btn" onClick={() => send('new_match', {})}>
              Play Again
            </button>
          ) : (
            <p style={{ color: '#94a3b8', fontStyle: 'italic', margin: 0, padding: '10px' }}>
              Waiting for host to start a new match...
            </p>
          )}
        </motion.div>
      </motion.div>
    </>
  );
}

function getRelPos(diff, total) {
  if (total === 4) {
    if (diff === 0) return 'bottom';
    if (diff === 1) return 'right';
    if (diff === 2) return 'top';
    if (diff === 3) return 'left';
  }
  if (total === 6) {
    if (diff === 0) return 'bottom';
    if (diff === 1) return 'bottom-right';
    if (diff === 2) return 'top-right';
    if (diff === 3) return 'top';
    if (diff === 4) return 'top-left';
    if (diff === 5) return 'bottom-left';
  }
  return 'hidden';
}

const getSeatStyle = (relPos) => {
  const base = { position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 5 };
  switch (relPos) {
    case 'bottom':       return { ...base, bottom: '2%', left: '50%', transform: 'translateX(-50%)', zIndex: 10 };
    case 'top':          return { ...base, top: '2%', left: '50%', transform: 'translateX(-50%)' };
    case 'left':         return { ...base, left: '2%', top: '50%', transform: 'translateY(-50%)', justifyContent: 'center' };
    case 'right':        return { ...base, right: '2%', top: '50%', transform: 'translateY(-50%)', justifyContent: 'center' };
    case 'top-left':     return { ...base, top: '10%', left: '15%' };
    case 'top-right':    return { ...base, top: '10%', right: '15%' };
    case 'bottom-left':  return { ...base, bottom: '15%', left: '15%' };
    case 'bottom-right': return { ...base, bottom: '15%', right: '15%' };
    default:             return { display: 'none' };
  }
};

const getPlayedCardStyle = (playerId, myId, totalPlayers, zIndex, sweepTarget) => {
  const diff = (playerId - myId + totalPlayers) % totalPlayers;
  
  const radius = 110; 
  let xOffset = 0;
  let yOffset = 0;
  
  // THE FIX: Decrease size by 5% (0.60 to 0.57) only for 4-player mode
  let scale = totalPlayers === 4 ? 0.57 : 0.6; 

  if (totalPlayers === 4) {
    if (diff === 0) { yOffset = radius; } 
    if (diff === 1) { xOffset = radius; } 
    if (diff === 2) { yOffset = -radius; } 
    if (diff === 3) { xOffset = -radius; } 
  } else if (totalPlayers === 6) {
    const radX = radius * 1.1; 
    if (diff === 0) { yOffset = radius; } 
    if (diff === 1) { xOffset = radX * 0.866; yOffset = radius * 0.5; } 
    if (diff === 2) { xOffset = radX * 0.866; yOffset = -radius * 0.5; } 
    if (diff === 3) { yOffset = -radius; } 
    if (diff === 4) { xOffset = -radX * 0.866; yOffset = -radius * 0.5; } 
    if (diff === 5) { xOffset = -radX * 0.866; yOffset = radius * 0.5; } 
  }

  let opacity = 1;
  let transitionStyle = 'transform 0.3s ease';

  if (sweepTarget === 'vanish') {
    xOffset = 0;
    yOffset = 0;
    scale = 0; 
    opacity = 0;
    transitionStyle = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease-in';
  } 
  else if (sweepTarget !== null && sweepTarget !== 'holding') {
    const sweepDiff = (sweepTarget - myId + totalPlayers) % totalPlayers;
    const sweepDist = 800; 
    opacity = 0; 
    transitionStyle = 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease-in';

    if (totalPlayers === 4) {
      if (sweepDiff === 0) { xOffset = 0; yOffset = sweepDist; }
      if (sweepDiff === 1) { xOffset = sweepDist; yOffset = 0; }
      if (sweepDiff === 2) { xOffset = 0; yOffset = -sweepDist; }
      if (sweepDiff === 3) { xOffset = -sweepDist; yOffset = 0; }
    } else if (totalPlayers === 6) {
      if (sweepDiff === 0) { xOffset = 0; yOffset = sweepDist; }
      if (sweepDiff === 1) { xOffset = sweepDist; yOffset = sweepDist * 0.5; }
      if (sweepDiff === 2) { xOffset = sweepDist; yOffset = -sweepDist * 0.5; }
      if (sweepDiff === 3) { xOffset = 0; yOffset = -sweepDist; }
      if (sweepDiff === 4) { xOffset = -sweepDist; yOffset = -sweepDist * 0.5; }
      if (sweepDiff === 5) { xOffset = -sweepDist; yOffset = sweepDist * 0.5; }
    }
  }

  return {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: `translate(calc(-50% + ${xOffset}px), calc(-50% + ${yOffset}px)) scale(${scale}) rotate(0deg)`,
    zIndex: zIndex,
    opacity: opacity,
    transition: transitionStyle
  };
};

export default function BoardDonkey({ state: gs, myPlayerId: truePlayerId, send, error, clearError, onLeave }) {
  const { playSound } = useAudio();
  
  const myId = gs?.your_index;
  const validPlays = useMemo(() => gs?.valid_plays || [], [gs?.valid_plays]);
  
  const [ghostTrick, setGhostTrick] = useState(null);
  const [sweepState, setSweepState] = useState(null); 
  const [frozenState, setFrozenState] = useState(null); 
  const [showTakeConfirm, setShowTakeConfirm] = useState(false);
  
  const prevTotalCards = useRef(0);
  const prevPhaseRef = useRef(null);
  const wasMyTurn = useRef(false);
  const playSoundRef = useRef(playSound);
  const gsRef = useRef(gs);

  useEffect(() => { playSoundRef.current = playSound; }, [playSound]);
  useEffect(() => { gsRef.current = gs; }, [gs]);

  useEffect(() => {
    if (gs?.phase === 'playing' && prevPhaseRef.current !== 'playing') {
      if (prevPhaseRef.current === null) {
        playSound('startGame');
      }
      playSound('cardShuffle');
    }
    prevPhaseRef.current = gs?.phase;
  }, [gs?.phase, playSound]);

  useEffect(() => {
    const totalInHands = gs?.hand_counts?.reduce((a, b) => a + (b || 0), 0) || 0;
    if (prevTotalCards.current > 0 && totalInHands < prevTotalCards.current) {
      if (gs?.phase === 'playing') playSound('cardPlay');
    }
    prevTotalCards.current = totalInHands;
  }, [gs?.hand_counts, gs?.phase, playSound]);

  useEffect(() => {
    const isMyTurnNow = gs?.phase === 'playing' && gs?.current_turn === myId;
    if (isMyTurnNow && !wasMyTurn.current) {
      playSound('yourTurn');
    }
    wasMyTurn.current = isMyTurnNow;
  }, [gs?.phase, gs?.current_turn, myId, playSound]);

  const prevTrickStr = JSON.stringify(gs?.previous_trick || []);

  useEffect(() => {
    if (prevTrickStr && prevTrickStr !== '[]') {
      const parsedTrick = JSON.parse(prevTrickStr);
      setGhostTrick(parsedTrick);
      setSweepState('holding'); 
      
      setFrozenState({ 
        hand: gsRef.current?.your_hand || [], 
        counts: gsRef.current?.hand_counts || [] 
      });
      
      const ledSuit = parsedTrick[0].card.suit;
      const isCut = parsedTrick.some(tc => tc.card.suit !== ledSuit);
      
      if (isCut) {
        playSoundRef.current('trumpReveal'); 
      }

      const holdTime = isCut ? 1500 : 400;
      const targetAction = isCut ? gsRef.current?.current_turn : 'vanish'; 

      const sweepTimer = setTimeout(() => {
        setSweepState(targetAction); 
      }, holdTime);

      const clearTimer = setTimeout(() => {
        setGhostTrick(null);
        setSweepState(null);
        setFrozenState(null); 
      }, holdTime + 600); 

      return () => { clearTimeout(sweepTimer); clearTimeout(clearTimer); };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevTrickStr]); 

  const isPlayable = useCallback((card) => {
    if (gs?.pending_take_request) return false;
    return validPlays.some(vc => vc.suit === card.suit && vc.rank === card.rank);
  }, [validPlays, gs?.pending_take_request]);

  if (!gs || !gs.players) return null;

  const players = gs.players;
  const totalPlayers = players.length || 4;
  const isMyTurn = gs.phase === 'playing' && gs.current_turn === myId;
  
  const activeHand = frozenState ? frozenState.hand : (gs.your_hand || []);
  const activeCounts = frozenState ? frozenState.counts : (gs.hand_counts || []);

  const sortedHand = [...activeHand].sort((a, b) => {
    const suitOrder = { 
      Spades: 1, S: 1, 
      Hearts: 2, H: 2, 
      Clubs: 3, C: 3, 
      Diamonds: 4, D: 4 
    };
    const rankPower = { '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14 };
    
    const suitA = suitOrder[a.suit] || 99;
    const suitB = suitOrder[b.suit] || 99;
    
    if (suitA !== suitB) {
      return suitA - suitB;
    }
    
    const rankA = rankPower[a.rank] || 0;
    const rankB = rankPower[b.rank] || 0;
    return rankA - rankB;
  });

  const opponents = [];
  for (let i = 1; i < totalPlayers; i++) {
    const oppId = (myId + i) % totalPlayers;
    const relPos = getRelPos(i, totalPlayers);
    const isVertical = relPos.includes('left') || relPos.includes('right');
    const isSafe = gs.is_safe && gs.is_safe[oppId];
    const handSize = activeCounts[oppId] || 0;

    opponents.push(
      <div key={oppId} style={getSeatStyle(relPos)}>
        <PlayerLabel 
          player={players[oppId]} 
          playerId={oppId} 
          gs={gs} 
          myId={myId} 
          send={send} 
        />
        <div style={{ marginTop: '8px', opacity: isSafe ? 0.5 : 1 }}>
          <AnimatedFaceDownHand 
            count={handSize} 
            vertical={isVertical} 
            playerIndex={i}
            myId={myId}
          />
        </div>
      </div>
    );
  }

  return (
    <LayoutGroup>
      <div className={`game-board players-${totalPlayers}`} style={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>

        <RoomControls onLeave={onLeave} />

        {opponents}

        {/* CENTRAL TRICK AREA */}
        <div style={{
            position: 'absolute', 
            // THE FIX: Moved 4-player trick area up to 48% to match 6-player
            top: totalPlayers === 6 ? '48%' : '48%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 1, 
            width: '400px', 
            height: '400px'
        }}>

          {ghostTrick && (
            <div style={{ pointerEvents: 'none', position: 'absolute', inset: 0 }}>
              {ghostTrick.map((tc, idx) => {
                const target = sweepState !== 'holding' ? sweepState : null;
                return (
                  <div key={`ghost-${idx}`} style={getPlayedCardStyle(tc.player_id, myId, totalPlayers, idx, target)}>
                    <AnimatedCard card={tc.card} layoutId={`ghost-${tc.card.suit}-${tc.card.rank}`} />
                  </div>
                );
              })}
            </div>
          )}

          {gs.current_trick?.map((tc, idx) => (
            <div key={`cur-${idx}`} style={getPlayedCardStyle(tc.player_id, myId, totalPlayers, idx + 10, null)}>
              <AnimatedCard card={tc.card} layoutId={`cur-${tc.card.suit}-${tc.card.rank}`} />
            </div>
          ))}
        </div>

        {/* YOUR HAND (Bottom) */}
        <div style={getSeatStyle('bottom')}>
          <div className="my-hand" style={{ transform: 'scale(0.85)', transformOrigin: 'bottom center', marginBottom: '-15px' }}>
            {sortedHand.map((card, idx) => {
              const cardId = `my-card-${idx}-${card.suit}-${card.rank}`;
              return (
                <AnimatedCard
                  key={cardId}
                  card={card}
                  layoutId={cardId}
                  className="card-wrapper"
                  playable={isMyTurn && isPlayable(card)}
                  onClick={isMyTurn && isPlayable(card) ? () => send('play_card', card) : null}
                  zIndexProp={idx + 10} 
                />
              );
            })}
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '8px' }}>
            <PlayerLabel 
              player={players[myId]} 
              playerId={myId} 
              gs={gs} 
              myId={myId} 
              send={send} 
            />
            {isMyTurn && gs.phase === 'playing' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '6px' }}>
                <div className="your-turn-hint">✨ Your turn</div>
                {!gs.pending_take_request && (
                  <button 
                    className="glow-btn" 
                    style={{ marginTop: '8px', padding: '6px 12px', fontSize: '0.9rem' }}
                    onClick={() => setShowTakeConfirm(true)}
                  >
                    Take Cards
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {showTakeConfirm && (
          <div className="phase-overlay" style={{ zIndex: 9999 }}>
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              className="match-over-panel"
            >
              <h3 style={{ color: 'white', marginBottom: '16px' }}>Take Cards?</h3>
              <p style={{ color: '#ccc', marginBottom: '24px' }}>
                Are you sure you want to ask the next player to take all their cards?
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <button 
                  className="glow-btn" 
                  style={{ background: '#3b82f6' }}
                  onClick={() => {
                    send('request_take_cards', {});
                    setShowTakeConfirm(false);
                  }}
                >
                  Yes
                </button>
                <button 
                  className="glow-btn" 
                  style={{ background: '#ef4444' }}
                  onClick={() => setShowTakeConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {gs.pending_take_request && (
          <div className="phase-overlay" style={{ zIndex: 9999 }}>
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              className="match-over-panel"
            >
              {gs.pending_take_request.target_id === myId ? (
                <>
                  <h3 style={{ color: 'white', marginBottom: '16px' }}>Take Cards Request</h3>
                  <p style={{ color: '#ccc', marginBottom: '24px' }}>
                    {players[gs.pending_take_request.requester_id]?.name || 'Player'} wants you to take all your cards. Accept?
                  </p>
                  <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                    <button 
                      className="glow-btn" 
                      style={{ background: '#10b981' }}
                      onClick={() => send('respond_take_cards', { accept: true })}
                    >
                      Accept
                    </button>
                    <button 
                      className="glow-btn" 
                      style={{ background: '#ef4444' }}
                      onClick={() => send('respond_take_cards', { accept: false })}
                    >
                      Reject
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 style={{ color: 'white', marginBottom: '16px' }}>Waiting...</h3>
                  <p style={{ color: '#ccc', marginBottom: '0' }}>
                    Waiting for {players[gs.pending_take_request.target_id]?.name || 'Player'} to respond to the request...
                  </p>
                </>
              )}
            </motion.div>
          </div>
        )}

        {gs.phase === 'match_over' && (
          <div className="phase-overlay" style={{ 
            zIndex: 9999, 
            paddingBottom: totalPlayers === 6 ? '12vh' : '0' 
          }}>
            <DonkeyMatchOver gs={gs} myId={myId} send={send} />
          </div>
        )}

        {error && <div className="error-toast" onClick={clearError}>⚠ {error}</div>}

      </div>
    </LayoutGroup>
  );
}